package com.appsyde.shared.presentation.screen.component.message

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.appsyde.shared.app.presentation.ticket.SelectedOutboundFile
import com.appsyde.shared.app.presentation.ticket.SendState
import com.appsyde.shared.modules.ticket.domain.attachment.OutboundAttachmentRules
import com.appsyde.shared.presentation.screen.component.message.helper.readFileAsBase64
import com.appsyde.shared.presentation.screen.component.message.outboundAttachmentIcon
import com.appsyde.shared.presentation.screen.component.message.formatFileSize
import com.appsyde.shared.presentation.style.style.AppStylesControls
import com.appsyde.shared.presentation.style.style.AppStylesMessages
import com.appsyde.shared.presentation.style.style.AppStylesProgress
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.marginTop
import org.jetbrains.compose.web.css.px
import com.appsyde.shared.presentation.style.animations.AppStylesAnimations
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.ExperimentalComposeWebSvgApi
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea
import org.jetbrains.compose.web.svg.Path
import org.jetbrains.compose.web.svg.Svg
import org.w3c.dom.HTMLTextAreaElement
import org.w3c.dom.events.Event
import org.w3c.files.File
import kotlin.random.Random

@OptIn(ExperimentalComposeWebSvgApi::class)

@Composable
fun AiBubbleEditable(
    text: String,
    onTextChange: (String) -> Unit,
    onEditingChange: (Boolean) -> Unit,
    /** Réponse générée par le backend (draft IA) ; au clic « Réponse suggérée » on remplit l’éditeur avec cette valeur, sans régénérer. */
    suggestedReplyDraft: String = "",
    /** true = dernier message envoyé par l'agent (on attend le client) → placeholder « En attente du client… » */
    awaitingClient: Boolean = false,
    onRegenerate: () -> Unit,
    isFirstLoadLoading: Boolean,
    isRegenerating: Boolean,
    sendState: SendState,
    onSend: () -> Unit,
    onFix: (String) -> Unit,
    onPro: (String) -> Unit,
    toneLabel: String,
    draftHeaderLabel: String,
    attachments: List<SelectedOutboundFile> = emptyList(),
    attachmentErrors: List<String> = emptyList(),
    onRemoveAttachment: (String) -> Unit = {},
    onAddAttachment: (SelectedOutboundFile) -> Unit = {},
    onSetAttachmentErrors: (List<String>) -> Unit = {},
    onClearAttachmentErrors: () -> Unit = {},
    onClearAllAttachments: () -> Unit = {}
) {
    var hasFocus by remember { mutableStateOf(false) }
    var elRef by remember { mutableStateOf<HTMLTextAreaElement?>(null) }
    var attachmentInputRef by remember { mutableStateOf<org.w3c.dom.HTMLInputElement?>(null) }
    var showFirstLoadOverlay by remember { mutableStateOf(false) }
    var activeAction by remember { mutableStateOf<String?>(null) }
    var isDraggingOver by remember { mutableStateOf(false) }
    var hoverAttachmentZone by remember { mutableStateOf(false) }
    var shakeTrigger by remember { mutableStateOf(0) }
    val scope = rememberCoroutineScope()
    val totalBytes = attachments.sumOf { it.sizeBytes }
    val totalMb = totalBytes / (1024 * 1024)
    val hasText = text.isNotBlank()
    val busy = isRegenerating || isFirstLoadLoading
    val maxTotalMb = OutboundAttachmentRules.MAX_TOTAL_BYTES / (1024 * 1024)

    LaunchedEffect(isRegenerating) {
        if (!isRegenerating) activeAction = null
    }

    fun triggerRefine(id: String, fire: () -> Unit) {
        if (busy) return
        if (!hasText) return
        if (activeAction != null) return
        activeAction = id
        fire()
    }

    LaunchedEffect(isFirstLoadLoading, text, hasFocus) {
        showFirstLoadOverlay = false
        if (isFirstLoadLoading && !hasFocus) {
            delay(150)
        }
    }

    Div(attrs = {
        classes(
            listOfNotNull(
                AppStylesMessages.bubbleDraft,
                AppStylesMessages.bubbleDraftInteractive,
                if (hasFocus) AppStylesMessages.bubbleDraftFocused else AppStylesMessages.bubbleDraftHoverable,
                if (isRegenerating) AppStylesMessages.bubbleDraftRegenerating else null
            )
        )
        ref { el ->
            if (el != null) {
                val onDragEnter: (Event) -> Unit = { ev ->
                    ev.preventDefault()
                    ev.stopPropagation()
                    isDraggingOver = true
                }
                val onDragOver: (Event) -> Unit = { ev -> ev.preventDefault() }
                val onDragLeave: (Event) -> Unit = { ev ->
                    ev.preventDefault()
                    val related = ev.asDynamic().relatedTarget
                    val stillInside = related != null && el.asDynamic().contains(related)
                    if (!stillInside) {
                        isDraggingOver = false
                    }
                }
                val onDrop = onDrop@{ ev: Event ->
                    ev.preventDefault()
                    isDraggingOver = false
                    val dt = ev.asDynamic().dataTransfer
                    val files = dt?.files ?: return@onDrop
                    val list = (0 until files.length).mapNotNull { files.item(it) as? File }
                    if (list.isEmpty()) return@onDrop
                    var runningTotal = attachments.sumOf { it.sizeBytes }
                    val errors = mutableListOf<String>()
                    for (file in list) {
                        val err = OutboundAttachmentRules.validateForAdd(
                            file.type.takeIf { it.isNotBlank() },
                            file.name,
                            file.size.toLong(),
                            runningTotal + file.size.toLong(),
                            attachments.size + list.size
                        )
                        if (err != null) {
                            errors.add(err)
                            shakeTrigger++
                        } else {
                            runningTotal += file.size.toLong()
                            scope.launch {
                                runCatching {
                                    val base64 = readFileAsBase64(file)
                                    onAddAttachment(
                                        SelectedOutboundFile(
                                            id = "out-${Random.nextLong()}",
                                            filename = file.name,
                                            sizeBytes = file.size.toLong(),
                                            mimeType = file.type.ifBlank { "application/octet-stream" },
                                            contentBase64 = base64
                                        )
                                    )
                                }
                            }
                        }
                    }
                    if (errors.isNotEmpty()) onSetAttachmentErrors(errors)
                }
                el.addEventListener("dragenter", onDragEnter)
                el.addEventListener("dragover", onDragOver)
                el.addEventListener("dragleave", onDragLeave)
                el.addEventListener("drop", onDrop)
                onDispose {
                    el.removeEventListener("dragenter", onDragEnter)
                    el.removeEventListener("dragover", onDragOver)
                    el.removeEventListener("dragleave", onDragLeave)
                    el.removeEventListener("drop", onDrop)
                }
            } else {
                onDispose { }
            }
        }
    }) {
        Div(attrs = { classes(AppStylesMessages.bubbleDraftTopRow) }) {
            Div(attrs = { classes(AppStylesMessages.bubbleDraftTopRowLeft) }) {
                Span(attrs = { classes(AppStylesMessages.bubbleDraftTopRowLabel) }) { Text("Répondre") }
                Span(attrs = { classes(AppStylesMessages.bubbleDraftTopRowHint) }) {
                    Text(if (awaitingClient) "En attente du client…" else "")
                }
                if (suggestedReplyDraft.isNotBlank() && !isRegenerating) {
                    Button(attrs = {
                        attr("type", "button")
                        classes(AppStylesMessages.bubbleFooterChipGhostStrong)
                        onClick { e ->
                            e.preventDefault()
                            e.stopPropagation()
                            elRef?.let { it.value = suggestedReplyDraft }
                            onTextChange(suggestedReplyDraft)
                            onEditingChange(true)
                            elRef?.focus()
                        }
                    }) {
                        Span(attrs = { classes(AppStylesMessages.toolbarChipIcon) }) { Text("✨") }
                        Span(attrs = { classes(AppStylesMessages.toolbarChipLabel) }) { Text("Réponse suggérée") }
                    }
                }
            }
            Div(attrs = { classes(AppStylesMessages.bubbleDraftTopRowActions) }) {
                Button(attrs = {
                    attr("type", "button")
                    classes(AppStylesMessages.bubbleDraftTopRowBtn, AppStylesMessages.bubbleDraftTopRowBtnDanger)
                    if (busy) attr("disabled", "true")
                    attr("title", "Effacer la réponse")
                    onClick { e ->
                        e.preventDefault()
                        e.stopPropagation()
                        if (busy) return@onClick
                        onTextChange("")
                        elRef?.focus()
                    }
                }) { Text("×") }
                Button(attrs = {
                    attr("type", "button")
                    classes(
                        listOfNotNull(
                            AppStylesMessages.bubbleDraftTopRowBtn,
                            AppStylesMessages.bubbleDraftTopRowBtnBrand,
                            if (isRegenerating) AppStylesMessages.toolbarIconButtonSpinning else null
                        )
                    )
                    if (isRegenerating) attr("disabled", "true")
                    attr("title", if (isRegenerating) "Régénération…" else "Régénérer la réponse")
                    onClick { e ->
                        e.preventDefault()
                        e.stopPropagation()
                        if (!isRegenerating) onRegenerate()
                    }
                }) { Text("↻") }
            }
        }

        Div(attrs = { classes(AppStylesMessages.bubbleDraftEditorWrap) }) {
             Div(attrs = { classes(AppStylesMessages.bubbleEditorInnerBox) }) {
            Div(attrs = { classes(AppStylesMessages.bubbleGhostArea) }) {
                Text(
                    when {
                        text.isEmpty() -> " "
                        text.endsWith("\n") -> text + " "
                        else -> text
                    }
                )
            }

            TextArea(
                value = text,
                attrs = {
                    id("answer-edit-area")
                    attr("placeholder",  "Rédigez votre réponse…")
                    attr("spellcheck", if (hasFocus) "true" else "false")
                    attr("autocapitalize", "sentences")
                    attr("autocomplete", "on")
                    attr("wrap", "soft")
                    attr("maxlength", "5000")

                    if (isRegenerating) attr("disabled", "true")

                    onFocusIn {
                        if (isRegenerating) return@onFocusIn
                        hasFocus = true
                        onEditingChange(true)
                    }

                    onFocusOut {
                        hasFocus = false
                        onEditingChange(false)
                    }

                    onInput { e ->
                        if (isRegenerating) return@onInput
                        val el = (e.target as? HTMLTextAreaElement) ?: return@onInput
                        elRef = el
                        onTextChange(el.value)
                    }

                    ref { el ->
                        elRef = el
                        onDispose { elRef = null }
                    }

                    classes(
                        listOfNotNull(
                            AppStylesMessages.bubbleEditableArea,
                            if (isRegenerating) AppStylesMessages.bubbleEditableAreaDimmed else null
                        )
                    )
                }
            )
            }

        if (showFirstLoadOverlay) {
            Div(attrs = { classes(AppStylesProgress.progressOverlay) }) {
                Div(attrs = { classes(AppStylesProgress.progressBarContainer) }) {
                    Div(attrs = { classes(AppStylesProgress.progressBarIndicator) }) {}
                }
            }
        }

        if (isDraggingOver) {
            Div(attrs = {
                classes(AppStylesMessages.bubbleDraftDropOverlay)
            }) {
                Span(attrs = { classes(AppStylesMessages.bubbleDraftDropOverlayPlus) }) { Text("+") }
            }
        }

        }

        Div(attrs = {
            classes(
                listOfNotNull(
                    AppStylesMessages.outboundAttachmentSection,
                    if (shakeTrigger > 0) AppStylesAnimations.attachmentDropShake else null
                )
            )
            onMouseEnter { hoverAttachmentZone = true }
            onMouseLeave { hoverAttachmentZone = false }
        }) {
            LaunchedEffect(shakeTrigger) {
                if (shakeTrigger > 0) {
                    delay(400)
                    shakeTrigger = 0
                }
            }
            val showAttachmentHeader =
                totalBytes > OutboundAttachmentRules.MAX_TOTAL_BYTES || attachmentErrors.isNotEmpty()
            if (showAttachmentHeader) {
                Div(attrs = { classes(AppStylesMessages.outboundAttachmentTitleRow) }) {
                    Span(attrs = { classes(AppStylesMessages.attachmentIcon) }) { Text("📎") }
                    Text("Pièces jointes")
                    Span(attrs = { classes(AppStylesMessages.attachmentSeparator) }) { Text("·") }
                    Text("${attachments.size}")
                    Span(attrs = { classes(AppStylesMessages.attachmentSeparator) }) { Text("·") }
                    Text("Total: $totalMb / $maxTotalMb Mo")
                    if (attachments.size > 3) {
                        Button(attrs = {
                            classes(AppStylesMessages.outboundAttachmentRemoveBtn)
                            attr("type", "button")
                            onClick { onClearAllAttachments() }
                        }) { Text("Tout retirer") }
                    }
                }
            }
            attachments.forEach { file ->
                Div(attrs = { classes(AppStylesMessages.outboundAttachmentFileRow) }) {
                    Span(attrs = {
                        classes(AppStylesMessages.outboundAttachmentFileIcon); attr(
                        "title",
                        file.mimeType
                    )
                    }) {
                        Text(outboundAttachmentIcon(file.mimeType, file.filename))
                    }
                    Span(attrs = { classes(AppStylesMessages.outboundAttachmentFileName) }) {
                        Text(
                            file.filename
                        )
                    }
                    Span(attrs = { classes(AppStylesMessages.outboundAttachmentFileSize) }) {
                        Text(formatFileSize(file.sizeBytes))
                    }
                    Button(attrs = {
                        classes(AppStylesMessages.outboundAttachmentRemoveBtn)
                        attr("type", "button")
                        attr("title", "Retirer")
                        onClick { onRemoveAttachment(file.id) }
                    }) { Text("×") }
                }
            }
            if (attachmentErrors.isNotEmpty()) {
                Div(attrs = { classes(AppStylesMessages.outboundAttachmentErrors) }) {
                    Text(attachmentErrors.joinToString("\n"))
                }
            }
        }

        // Row : Grammaire/Ton et Envoyer (« Réponse suggérée » est dans le header)
        Div(attrs = { classes(AppStylesMessages.bubbleFooterRow) }) {
            Div(attrs = { classes(AppStylesMessages.bubbleFooterRowInner) }) {
                Div(attrs = { classes(AppStylesMessages.bubbleFooterActions) }) {
                    Button(attrs = {
                        attr("type", "button")
                        classes(
                            listOfNotNull(
                                AppStylesMessages.bubbleFooterChipGhost,
                                if (activeAction == "fix") AppStylesMessages.toolbarChipLoading else null
                            )
                        )
                        if (busy || activeAction != null || !hasText) attr("disabled", "true")
                        onClick { e ->
                            e.preventDefault()
                            e.stopPropagation()
                            triggerRefine("fix") { onFix(text) }
                        }
                    }) {
                        Span(attrs = { classes(AppStylesMessages.toolbarChipIcon) }) { Text("📖") }
                        Span(attrs = { classes(AppStylesMessages.toolbarChipLabel) }) { Text("Corriger") }
                    }
                    Button(attrs = {
                        attr("type", "button")
                        classes(
                            listOfNotNull(
                                AppStylesMessages.bubbleFooterChipGhost,
                                if (activeAction == "pro") AppStylesMessages.toolbarChipLoading else null
                            )
                        )
                        if (busy || activeAction != null || !hasText) attr("disabled", "true")
                        onClick { e ->
                            e.preventDefault()
                            e.stopPropagation()
                            triggerRefine("pro") { onPro(text) }
                        }
                    }) {
                        Span(attrs = { classes(AppStylesMessages.toolbarChipIcon) }) { Text("🎯") }
                        Span(attrs = { classes(AppStylesMessages.toolbarChipLabel) }) { Text("Ajuster le ton") }
                    }
                }
                Div(attrs = {
                    style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(10.px) }
                }) {
                    Input(InputType.File, attrs = {
                        id("outbound-attachment-input")
                        attr("multiple", "true")
                        style { property("display", "none") }
                        ref { el ->
                            attachmentInputRef = el
                            onDispose { attachmentInputRef = null }
                        }
                        onChange { e ->
                            val input = e.target as? org.w3c.dom.HTMLInputElement ?: return@onChange
                            val files = input.files ?: return@onChange
                            val list = (0 until files.length).mapNotNull { files.item(it) as? File }
                            input.value = ""
                            if (list.isEmpty()) return@onChange
                            var currentTotal = attachments.sumOf { it.sizeBytes }
                            val errors = mutableListOf<String>()
                            for (file in list) {
                                val err = OutboundAttachmentRules.validateForAdd(
                                    file.type.takeIf { it.isNotBlank() },
                                    file.name,
                                    file.size.toLong(),
                                    currentTotal + file.size.toLong(),
                                    attachments.size + list.size
                                )
                                if (err != null) {
                                    errors.add(err)
                                } else {
                                    currentTotal += file.size.toLong()
                                    scope.launch {
                                        runCatching {
                                            val base64 = readFileAsBase64(file)
                                            onAddAttachment(
                                                SelectedOutboundFile(
                                                    id = "out-${Random.nextLong()}",
                                                    filename = file.name,
                                                    sizeBytes = file.size.toLong(),
                                                    mimeType = file.type.ifBlank { "application/octet-stream" },
                                                    contentBase64 = base64
                                                )
                                            )
                                        }
                                    }
                                }
                            }
                            if (errors.isNotEmpty()) onSetAttachmentErrors(errors)
                        }
                    })
                    Button(attrs = {
                        attr("type", "button")
                        attr("title", "Ajouter une pièce jointe")
                        classes(
                            AppStylesMessages.toolbarIconButton,
                            AppStylesMessages.bubbleDraftButtonBg
                        )
                        if (busy) attr("disabled", "true")
                        onClick { e ->
                            e.preventDefault()
                            attachmentInputRef?.click()
                        }
                    }) {
                        Svg(
                            viewBox = "0 0 24 24",
                            attrs = {
                                style { property("width", "18px"); property("height", "18px") }
                                attr("fill", "currentColor")
                            }
                        ) {
                            Path(d = "M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5C7 20.54 9.46 23 12.5 23s5.5-2.46 5.5-5.5V6h-1.5z")
                        }
                    }
                    Button(attrs = {
                        val sendDisabled = text.isEmpty() || sendState is SendState.Sending
                        if (sendState is SendState.Sending) {
                            classes(
                                AppStylesControls.btnPrimary,
                                AppStylesControls.btnPrimaryLoading
                            )
                        } else if (text.isEmpty()) {
                            classes(AppStylesControls.sendButtonDisabled)
                        } else {
                            classes(AppStylesControls.btnPrimary)
                        }
                        if (sendDisabled) attr("disabled", "true")
                        onClick { onSend() }
                        attr("type", "button")
                    }) {
                        when (sendState) {
                            is SendState.Idle -> {
                                Span(attrs = { classes(AppStylesControls.sendBtnContent) }) {
                                    Text("Envoyer")
                                    Svg(
                                        viewBox = "0 0 24 24",
                                        attrs = {
                                            classes(AppStylesControls.sendIconSvg)
                                            attr("fill", "currentColor")
                                        }
                                    ) {
                                        Path(d = "M2.01 21L23 12 2.01 3 2 10l15 2-15 2v7z")
                                    }
                                }
                            }

                            is SendState.Sending -> {
                                Span(attrs = { classes(AppStylesControls.sendSpinner) })
                                Span(attrs = { classes(AppStylesControls.sendSpinnerText) }) {
                                    Text(
                                        "Envoi..."
                                    )
                                }
                            }

                            is SendState.Success -> {
                                Span(attrs = { classes(AppStylesControls.sendSuccessIcon) }) {
                                    Text(
                                        "✓"
                                    )
                                }
                                Span(attrs = { classes(AppStylesControls.sendSuccessText) }) {
                                    Text(
                                        "Envoyé"
                                    )
                                }
                            }

                            is SendState.Error -> {
                                Span(attrs = { classes(AppStylesControls.sendErrorText) }) { Text("Réessayer") }
                            }
                        }
                    }
                }
            }
        }
    }
}

